"use client";

import React, { useState } from 'react';
import { Menu, Bell, Search, Globe, AlertTriangle, RefreshCw } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';

interface TopbarProps {
  sidebarCollapsed: boolean;
  onMobileMenuOpen: () => void;
  alertBadgeCount?: number;
  onAlertBadgeClear?: () => void;
}

export default function Topbar({ sidebarCollapsed, onMobileMenuOpen, alertBadgeCount = 0, onAlertBadgeClear }: TopbarProps) {
  const { language, toggleLanguage } = useLanguage();
  const [notifOpen, setNotifOpen] = useState(false);

  const notifications = [
    { id: 1, level: 'critical', message: 'SQL Injection attempt on /api/v2/users — blocked', time: '2m ago' },
    { id: 2, level: 'high', message: 'Brute force detected from 103.47.22.91 — 847 attempts', time: '8m ago' },
    { id: 3, level: 'medium', message: 'BD Compliance Section 18 review due in 3 days', time: '1h ago' },
  ];

  const totalBadge = alertBadgeCount + notifications.length;

  const handleBellClick = () => {
    setNotifOpen(!notifOpen);
    if (!notifOpen && onAlertBadgeClear) {
      onAlertBadgeClear();
    }
  };

  return (
    <header className="h-14 bg-[#080B12]/90 border-b border-cyber-border backdrop-blur-sm flex items-center px-4 md:px-6 gap-4 shrink-0 z-20" role="banner">
      {/* Mobile menu button */}
      <button
        onClick={onMobileMenuOpen}
        aria-label="Open navigation menu"
        className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
      >
        <Menu size={18} className="text-cyber-muted" />
      </button>

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-white/5 border border-cyber-border rounded-lg px-3 py-1.5 w-64 hover:border-cyber-blue/30 transition-colors group" role="search">
        <Search size={14} className="text-cyber-muted group-hover:text-cyber-blue transition-colors" />
        <input
          type="text"
          placeholder="Search threats, CVEs, assets..."
          aria-label="Search threats, CVEs, and assets"
          className="bg-transparent text-xs text-cyber-text placeholder-cyber-muted outline-none flex-1 font-mono"
        />
        <span className="text-[10px] text-cyber-subtle font-mono border border-cyber-border rounded px-1" aria-label="Keyboard shortcut Command K">⌘K</span>
      </div>

      {/* Threat Level Indicator */}
      <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/25 rounded-lg" role="status" aria-label="Current threat level: Elevated">
        <AlertTriangle size={13} className="text-orange-400" />
        <span className="text-[11px] font-mono text-orange-400 font-medium">THREAT LEVEL: ELEVATED</span>
      </div>

      <div className="flex-1" />

      {/* Last Sync */}
      <div className="hidden lg:flex items-center gap-1.5 text-[10px] text-cyber-muted font-mono" role="status" aria-label="Last synced 23 seconds ago">
        <RefreshCw size={11} className="text-cyber-green" />
        <span>Synced 23s ago</span>
      </div>

      {/* Language Toggle */}
      <button
        onClick={toggleLanguage}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-cyber-border rounded-lg hover:bg-cyber-blue/10 hover:border-cyber-blue/30 transition-all duration-150 group"
        aria-label={`Switch language to ${language === 'en' ? 'Bangla' : 'English'}`}
        title={`Switch to ${language === 'en' ? 'Bangla' : 'English'}`}
      >
        <Globe size={13} className="text-cyber-muted group-hover:text-cyber-blue transition-colors" />
        <span className="text-[11px] font-medium text-cyber-muted group-hover:text-cyber-blue transition-colors font-mono">
          {language === 'en' ? 'বাং' : 'EN'}
        </span>
      </button>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={handleBellClick}
          className="relative w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
          aria-label={`Notifications${totalBadge > 0 ? ` (${totalBadge} unread)` : ''}`}
        >
          <Bell size={16} className={totalBadge > 0 ? 'text-cyber-text' : 'text-cyber-muted'} />
          {/* Alert badge with live count */}
          {totalBadge > 0 ? (
            <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full border border-[#080B12] text-[9px] font-bold font-mono px-0.5 transition-all duration-300 ${
              alertBadgeCount > 0 ? 'bg-cyber-red text-white animate-pulse' : 'bg-cyber-red text-white'
            }`}>
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          ) : (
            <span className="absolute top-1 right-1 w-2 h-2 bg-cyber-red rounded-full border border-[#080B12]" />
          )}
        </button>

        {notifOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
            <div className="absolute right-0 top-10 w-80 bg-[#0F1117] border border-cyber-border rounded-xl shadow-2xl z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-cyber-border flex items-center justify-between">
                <span className="text-sm font-semibold text-cyber-text">Alerts</span>
                <div className="flex items-center gap-2">
                  {alertBadgeCount > 0 && (
                    <span className="text-[10px] font-mono text-cyber-red bg-cyber-red/10 border border-cyber-red/20 rounded-full px-2 py-0.5 animate-pulse">
                      {alertBadgeCount} live
                    </span>
                  )}
                  <span className="text-[10px] font-mono text-cyber-blue bg-cyber-blue/10 border border-cyber-blue/20 rounded-full px-2 py-0.5">
                    {notifications.length} new
                  </span>
                </div>
              </div>

              {/* Live threat badge section */}
              {alertBadgeCount > 0 && (
                <div className="px-4 py-2.5 bg-red-950/40 border-b border-red-500/20 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyber-red animate-pulse shrink-0" />
                  <p className="text-[11px] text-red-300 font-mono">
                    {alertBadgeCount} new high-severity threat{alertBadgeCount > 1 ? 's' : ''} detected
                  </p>
                </div>
              )}

              <div className="divide-y divide-cyber-border">
                {notifications.map((n) => (
                  <div key={n.id} className="px-4 py-3 hover:bg-white/3 transition-colors cursor-pointer">
                    <div className="flex items-start gap-2">
                      <div className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                        n.level === 'critical' ? 'bg-cyber-red' :
                        n.level === 'high' ? 'bg-orange-400' : 'bg-yellow-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-cyber-text leading-snug">{n.message}</p>
                        <p className="text-[10px] text-cyber-muted mt-1 font-mono">{n.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2.5 border-t border-cyber-border">
                <button
                  className="text-xs text-cyber-blue hover:text-cyber-blue/80 transition-colors"
                  aria-label="View all alerts"
                >
                  View all alerts →
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* User Avatar */}
      <div className="flex items-center gap-2.5 pl-2 border-l border-cyber-border">
        <div className="w-7 h-7 rounded-full bg-cyber-blue/20 border border-cyber-blue/30 flex items-center justify-center">
          <span className="text-[10px] font-bold text-cyber-blue">OK</span>
        </div>
        <div className="hidden md:block">
          <p className="text-xs font-medium text-cyber-text leading-tight">Opel Khan</p>
          <p className="text-[10px] text-cyber-muted leading-tight font-mono">CISO</p>
        </div>
      </div>
    </header>
  );
}